// Modules
import { Link, useNavigate } from "react-router-dom"

// Constants
import PATHS, { getFullPath } from "~constants/paths"

// Components
import ListItemMenu from "~components/ListItemMenu"
import LoadingTable from "~components/LoadingTable"
import { CompanyLogo } from "./styled/company"
import SkeletonWrapper from "~components/SkeletonWrapper"
import { ActiveStatus, InactiveStatus } from "~styled/status-badget"
import {
  Th,
  Td,
  Table,
  TableLink,
  NoTableData,
  TableContainer,
  Tr,
} from "~components/styled/table"

// Utils
import { isCompanyActive } from "./utils/company"
import { getFullImagePath } from "~utils/utils"

// Shapes
import { LocationShape } from "~shapes/location"
import { CompanyShape } from "~shapes/company"

const companyDetailPath = (id: string): string => {
  return `${getFullPath(PATHS.companies)}/${id}`
}

export interface CompaniesListProps {
  companies: CompanyShape[]
  isLoading: boolean
  onClickCompanyEdit: (id: string) => void
  onChangeStatus: (id: string, status: string) => void
}

const CompaniesList = (props: CompaniesListProps) => {
  const { companies, isLoading, onClickCompanyEdit, onChangeStatus } = props
  const navigate = useNavigate()

  const isFirstLoad: boolean = !companies.length && isLoading
  const noCompaniesFound: boolean = !companies.length && !isLoading

  const statusIndicator = (company: CompanyShape) =>
    isCompanyActive(company) ? <ActiveStatus /> : <InactiveStatus />

  const handleCompanyEdit = (id: string) => {
    onClickCompanyEdit(id)
  }

  const handleCompanyView = (id: string) => {
    navigate(companyDetailPath(id), { replace: false })
  }

  const handleToggleStatus = (company: CompanyShape) => {
    if (company.id) {
      const status: string = company.status === "Active" ? "Inactive" : "Active"
      onChangeStatus(company.id, status)
    }
  }

  const handleViewCompanyMessages = (company: CompanyShape) => {
    const messagesPath = PATHS.companyMessages.replace(
      ":id",
      company.id as string
    )
    navigate(getFullPath(messagesPath), {
      replace: false,
      state: { company: company },
    })
  }

  return (
    <TableContainer>
      <Table>
        <thead>
          <tr>
            <Th style={{ width: "40px" }} />
            <Th style={{ width: "90px" }} />
            <Th style={{ minWidth: "150px", width: "25%" }}>Name</Th>
            <Th style={{ minWidth: "75px", width: "15%" }}>Code</Th>
            <Th style={{ minWidth: "120px", width: "15%" }}>Exchange</Th>
            <Th style={{ minWidth: "130px", width: "10%" }}>Symbol</Th>
            <Th style={{ minWidth: "100px", width: "10%" }}>Country</Th>
            <Th style={{ minWidth: "80px", width: "5%" }} />
          </tr>
        </thead>

        <tbody>
          {/* Loading */}
          {isFirstLoad && (
            <LoadingTable rowHeight="75px" columns={10}>
              <Td>
                <SkeletonWrapper isLoading={true} className="skeleton-status" />
              </Td>
              <Td>
                <SkeletonWrapper
                  isLoading={true}
                  className="skeleton-image"
                  width={60}
                />
              </Td>
            </LoadingTable>
          )}

          {/* Main Table */}
          {companies &&
            companies.map(company => {
              const companyLocation = company.location as LocationShape

              return (
                <Tr key={company.id}>
                  {/* Status */}
                  <Td>
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-status"
                    >
                      {statusIndicator(company)}
                    </SkeletonWrapper>
                  </Td>

                  {/* Logo */}
                  <Td>
                    <SkeletonWrapper
                      isLoading={isLoading}
                      className="skeleton-image"
                      width={60}
                    >
                      <CompanyLogo src={getFullImagePath(company?.logo)} />
                    </SkeletonWrapper>
                  </Td>

                  {/* Name */}
                  <Td style={{ maxWidth: "150px" }}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <TableLink>
                        <Link to={companyDetailPath(company.id!)}>
                          {company.name}
                        </Link>
                      </TableLink>
                    </SkeletonWrapper>
                  </Td>

                  {/* Code */}
                  <Td style={{ maxWidth: "150px" }}>
                    <SkeletonWrapper isLoading={isLoading}>
                      <TableLink>
                        <Link to={companyDetailPath(company.id!)}>
                          {company.code}
                        </Link>
                      </TableLink>
                    </SkeletonWrapper>
                  </Td>

                  {/* Exchange */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      <span>{company.exchange}</span>
                    </SkeletonWrapper>
                  </Td>

                  {/* Symbol */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      <span>{company.symbol}</span>
                    </SkeletonWrapper>
                  </Td>

                  {/* Country */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      <span>{companyLocation.country || "--"}</span>
                    </SkeletonWrapper>
                  </Td>

                  {/* Actions */}
                  <Td>
                    <SkeletonWrapper isLoading={isLoading}>
                      <ListItemMenu
                        isActive={isCompanyActive(company)}
                        onEdit={() => handleCompanyEdit(company.id as string)}
                        // onView={() => handleCompanyView(company.id as string)}
                        onToggleStatus={() => handleToggleStatus(company)}
                        onMessagesView={() =>
                          handleViewCompanyMessages(company)
                        }
                      />
                    </SkeletonWrapper>
                  </Td>
                </Tr>
              )
            })}
        </tbody>
      </Table>

      {/* No results found */}
      {noCompaniesFound && <NoTableData>No companies found...</NoTableData>}
    </TableContainer>
  )
}

export default CompaniesList
